/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { INLINES } from '@contentful/rich-text-types';
import type { Block, Inline, Text } from '@contentful/rich-text-types';

import { MOMENT_FORMATS, formatDateTimeDisplay } from 'js/utils/DateTimeUtils/DateTimeUtils';

import { Link, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { isExternalLink } from 'bundles/common/utils/urlUtils';
import type { NewsItemFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import RichText from 'bundles/degree-description/components/shared/RichText';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';

import _t from 'i18n!nls/degree-description';

type Props = {
  title: NewsItemFragment['title'];
  description: NewsItemFragment['body'];
  newsDate: NewsItemFragment['postDate'];
  timeToRead: NewsItemFragment['readLength'];
  url: NewsItemFragment['url'];
};

const styles: Record<string, Interpolation<Theme>> = {
  card: css`
    position: relative;
    background-color: var(--cds-color-neutral-primary-invert);
    border: 2px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: var(--cds-border-radius-100);
    padding: calc(var(--cds-spacing-200) + var(--cds-spacing-50));

    :hover {
      border: 2px solid var(--cds-color-neutral-stroke-primary);
    }
  `,
  link: css`
    :hover,
    &:focus {
      text-decoration: none;
    }

    &::before {
      content: '';
      cursor: inherit;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      width: 100%;
      height: 100%;
    }
  `,
  title: css`
    display: -webkit-box;
    height: 72px;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
  `,
  description: css`
    p {
      display: -webkit-box;
      overflow: hidden;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
    }
  `,
  inlineLink: css`
    position: relative;
    z-index: 1;
  `,
};

const RelatedNewsCard = ({ title, description, newsDate, timeToRead, url }: Props) => {
  const { trackClickButton } = useDegreeDescriptionEventingContext();

  const handleOnClick = () => {
    trackClickButton('degree_related_news', 'latest_news', url, title);
  };

  const richTextRenderOptions = {
    renderNode: {
      [INLINES.HYPERLINK]: (node: Block | Inline) => {
        const textBlock = node?.content?.[0] as Text;

        return (
          <Link
            variant="quiet"
            typographyVariant="subtitleMedium"
            href={node.data.uri}
            target={isExternalLink(node.data.uri) ? '_blank' : undefined}
            rel={isExternalLink(node.data.uri) ? 'noopener noreferrer' : undefined}
            css={styles.inlineLink}
          >
            {textBlock?.value}
          </Link>
        );
      },
    },
  };

  return (
    <div css={styles.card}>
      <TrackedLink2
        href={url}
        target={isExternalLink(url ?? '') ? '_blank' : undefined}
        rel={isExternalLink(url ?? '') ? 'noopener noreferrer' : undefined}
        trackingName="related_news"
        data={{
          title,
        }}
        css={styles.link}
        onClick={handleOnClick}
      >
        {title && (
          <Typography2 component="h3" css={styles.title} variant="subtitleMedium">
            {title}
          </Typography2>
        )}
      </TrackedLink2>
      {description && (
        <div css={styles.description}>
          <RichText content={description?.json} richTextRenderOptions={richTextRenderOptions} />
        </div>
      )}
      <div>
        {newsDate && (
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {formatDateTimeDisplay(newsDate, MOMENT_FORMATS.LONG_DATE_ONLY_DISPLAY)}
          </Typography2>
        )}

        {timeToRead && (
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {_t('Article · #{readTimeInMinutes} min read', {
              readTimeInMinutes: timeToRead,
            })}
          </Typography2>
        )}
      </div>
    </div>
  );
};

export default RelatedNewsCard;
